import React, { useCallback, useRef } from 'react';
import { useForm, Controller, FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Checkbox } from '@frontend/shadcn/components/ui/checkbox';
import { Label } from '@frontend/shadcn/components/ui/label';
import { Tooltip, TooltipTrigger } from '@frontend/shadcn/components/ui/tooltip';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@frontend/shadcn/components/ui/form';
import { CircleInfoIcon } from '@revfluence/fresh-icons/solid/esm';
import { FiltersFormProps } from './types';
import {
  TooltipHeader,
  TooltipTitle,
  TooltipBody,
  TooltipItem,
  TooltipItemValue,
  TooltipArrow,
  TooltipContent,
} from '../../../components/tooltip';

import type { SegmentValue } from './segments-select';
import { SegmentsOptions, SegmentsSelect } from './segments-select';
import { PostTypesSelect, PostTypesOptions } from './post-types-select';
import { NetworksSelect, NetworksOptions } from './networks-select';
import { GroupsSelect, GroupsOptions } from './groups-select';
import { ProjectsSelect, ProjectsOptions } from './projects-select';
import { MembersSelect, MembersOptions } from './members-select';
import type { MentionSourceValue } from './mention-source-select';
import { MentionSourceSelect, MentionSourceOptions } from './mention-source-select';
import { DateFilterMethodSelect, DateFilterMethodOptions } from './date-filter-method-select';

export const filtersFormSchema = z
  .object({
    filterType: z.enum(['network', 'posts']).default('network'),
    networks: z.array(z.enum(['INSTAGRAM', 'TIKTOK', 'PINTEREST', 'YOUTUBE'])).default([]),
    projectIds: z.array(z.number()).default([]),
    groupIds: z.array(z.number()).default([]),
    memberIds: z.array(z.string()).default([]),
    postTypes: z
      .array(
        z.enum([
          'INSTAGRAM_POST',
          'INSTAGRAM_REEL',
          'INSTAGRAM_STORY',
          'TIKTOK_VIDEO',
          'YOUTUBE_VIDEO',
          'YOUTUBE_SHORT',
          'PINTEREST_PIN',
        ]),
      )
      .default([]),
    includeEstimates: z.boolean().default(false),
    includeAdsData: z.boolean().default(false),
    postDateIntervalEnabled: z.boolean().default(false),
    lifetimeInsightsEnabled: z.boolean().default(false),
    // includeUnassigned: z.boolean().default(false),
    segments: z.array(z.enum(['brandFans', 'macro', 'mega', 'micro', 'midTier', 'nano'])).default([]),
    mentionSources: z.array(z.enum(['brand', 'creator', 'socialListening'])).default([]),
  })
  .refine((data) => !(data.networks.length > 0 && data.postTypes.length > 0), {
    message: 'Please select either Networks or Post Types, but not both',
    path: ['network'],
  });
export type FiltersFormData = z.infer<typeof filtersFormSchema>;

export type NetworkValue = z.infer<typeof filtersFormSchema>['networks'][number];
export type PostTypeValue = z.infer<typeof filtersFormSchema>['postTypes'][number];

export const networkOptions: Array<{ value: NetworkValue; label: string }> = [
  { value: 'INSTAGRAM', label: 'Instagram' },
  { value: 'TIKTOK', label: 'TikTok' },
  { value: 'PINTEREST', label: 'Pinterest' },
  { value: 'YOUTUBE', label: 'YouTube' },
];

export const postTypeOptions: Array<{ value: PostTypeValue; label: string }> = [
  { value: 'INSTAGRAM_POST', label: 'Instagram Post' },
  { value: 'INSTAGRAM_REEL', label: 'Instagram Reel' },
  { value: 'INSTAGRAM_STORY', label: 'Instagram Story' },
  { value: 'TIKTOK_VIDEO', label: 'TikTok Video' },
  { value: 'YOUTUBE_VIDEO', label: 'YouTube Video' },
  { value: 'YOUTUBE_SHORT', label: 'YouTube Short' },
  { value: 'PINTEREST_PIN', label: 'Pinterest Pin' },
];

export const mentionSourceOptions: Array<{ value: MentionSourceValue; label: string }> = [
  { value: 'brand', label: 'Brand' },
  { value: 'creator', label: 'Creator' },
  { value: 'socialListening', label: 'Social Listening' },
];

export const dateFilterMethodOptions: Array<{ value: DateFilterMethodValue; label: string; description: string }> = [
  {
    value: 'insight_date',
    label: 'Insight Date',
    description: 'View impressions and engagements that happened during the selected date range, regardless of when the posts were originally published.'
  },
  {
    value: 'post_date_current',
    label: 'Post Date: Current Period',
    description: 'View impressions and engagements for posts that were published during the selected date range, showing only the activity that occurred within that same time period.'
  },
  {
    value: 'post_date_lifetime',
    label: 'Post Date: Lifetime Data',
    description: 'View all-time impressions and engagements for posts that were published during the selected date range, including activity from their publish date through today.'
  }
];

export type DateFilterMethodValue = 'insight_date' | 'post_date_current' | 'post_date_lifetime';

export const FiltersForm: React.FC<FiltersFormProps> = ({
  projects,
  groups,
  members,
  setMemberSearchTerm,
  isFetchingProjects,
  isFetchingGroups,
  isFetchingMembers,
  defaultValues,
  onSubmit,
  onCancel,
}) => {
  const form = useForm<z.infer<typeof filtersFormSchema>>({
    resolver: zodResolver(filtersFormSchema),
    defaultValues: {
      networks: defaultValues?.networks || [],
      postTypes: defaultValues?.postTypes || [],
      includeEstimates: defaultValues?.includeEstimates || false,
      includeAdsData: defaultValues?.includeAdsData || false,
      filterType: defaultValues?.postTypes?.length ? 'posts' : 'network',
      memberIds: defaultValues?.memberIds || [],
      projectIds: defaultValues?.projectIds || [],
      groupIds: defaultValues?.groupIds || [],
      // includeUnassigned: defaultValues?.includeUnassigned || false,
      segments: defaultValues?.segments || [],
      mentionSources: defaultValues?.mentionSources || [],
      postDateIntervalEnabled: defaultValues?.postDateIntervalEnabled || false,
      lifetimeInsightsEnabled: defaultValues?.lifetimeInsightsEnabled || false,
    },
  });

  // Debounce timer reference for search
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced search handler for members
  const handleMemberSearch = useCallback((value: string) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Immediately set empty value without debounce to reset members
    if (!value || value.length === 0) {
      setMemberSearchTerm('');
      return;
    }

    debounceTimerRef.current = setTimeout(() => {
      setMemberSearchTerm(value);
    }, 300);
  }, [setMemberSearchTerm]);

  const handleSubmit = (data: z.infer<typeof filtersFormSchema>) => {
    onSubmit(data);
  };

  const handleError = (errors: FieldErrors<z.infer<typeof filtersFormSchema>>) => {
    console.error('Form validation errors:', errors);
  };

  const networks = form.watch('networks');
  const postTypes = form.watch('postTypes');
  const postDateIntervalEnabled = form.watch('postDateIntervalEnabled');
  const lifetimeInsightsEnabled = form.watch('lifetimeInsightsEnabled');

  // Determine current date filter method based on form values
  const getCurrentDateFilterMethod = (): DateFilterMethodValue => {
    if (postDateIntervalEnabled && lifetimeInsightsEnabled) {
      return 'post_date_lifetime';
    } else if (postDateIntervalEnabled) {
      return 'post_date_current';
    } else {
      return 'insight_date';
    }
  };

  // Handle date filter method selection
  const handleDateFilterMethodChange = (value: DateFilterMethodValue) => {
    switch (value) {
      case 'insight_date':
        form.setValue('postDateIntervalEnabled', false);
        form.setValue('lifetimeInsightsEnabled', false);
        break;
      case 'post_date_current':
        form.setValue('postDateIntervalEnabled', true);
        form.setValue('lifetimeInsightsEnabled', false);
        break;
      case 'post_date_lifetime':
        form.setValue('postDateIntervalEnabled', true);
        form.setValue('lifetimeInsightsEnabled', true);
        break;
    }
  };

  // Clear the other field when one is selected
  React.useEffect(() => {
    if (networks.length > 0) {
      form.setValue('postTypes', []);
    }
  }, [networks, form]);

  React.useEffect(() => {
    if (postTypes.length > 0) {
      form.setValue('networks', []);
    }
  }, [postTypes, form]);

  return (
    <form onSubmit={form.handleSubmit(handleSubmit, handleError)} className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto">
        <Form {...form}>
          <div className="space-y-2 mb-2 mt-4">
            <Label>Member</Label>
            <FormField
              control={form.control}
              name="memberIds"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <MembersSelect>
                      <MembersOptions
                        members={members || []}
                        isLoading={isFetchingMembers}
                        value={field.value}
                        onChange={field.onChange}
                        onSearch={handleMemberSearch}
                      />
                    </MembersSelect>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="space-y-2 mb-2 mt-4">
            <Label>Projects</Label>
            <FormField
              control={form.control}
              name="projectIds"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ProjectsSelect>
                      <ProjectsOptions
                        projects={projects}
                        isLoading={isFetchingProjects}
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </ProjectsSelect>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="space-y-2 mb-2">
            <Label>Groups</Label>
            <FormField
              control={form.control}
              name="groupIds"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <GroupsSelect>
                      <GroupsOptions
                        groups={groups}
                        isLoading={isFetchingGroups}
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </GroupsSelect>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="space-y-2 mb-2">
            <Label>Networks</Label>
            <FormField
              control={form.control}
              name="networks"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <NetworksSelect>
                      <NetworksOptions
                        options={networkOptions}
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </NetworksSelect>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="space-y-2 mb-2">
            <FormField
              control={form.control}
              name="postTypes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Post Types</FormLabel>
                  <FormControl>
                    <PostTypesSelect>
                      <PostTypesOptions
                        options={postTypeOptions}
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </PostTypesSelect>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="space-y-2 mb-2">
            <Label>Creator Tier</Label>
            <FormField
              control={form.control}
              name="segments"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <SegmentsSelect>
                      <SegmentsOptions
                        value={field.value as SegmentValue[]}
                        onChange={field.onChange}
                      />
                    </SegmentsSelect>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="space-y-2 mb-2">
            <Label>Content Sources</Label>
            <FormField
              control={form.control}
              name="mentionSources"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <MentionSourceSelect>
                      <MentionSourceOptions
                        value={field.value as MentionSourceValue[]}
                        onChange={field.onChange}
                      />
                    </MentionSourceSelect>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="space-y-2 mb-2">
            <Label>Date Filter Method</Label>
            <DateFilterMethodSelect>
              <DateFilterMethodOptions
                options={dateFilterMethodOptions}
                value={getCurrentDateFilterMethod()}
                onChange={handleDateFilterMethodChange}
              />
            </DateFilterMethodSelect>
          </div>
          <div className="space-y-2 mb-2">
            <Controller
              name="includeEstimates"
              control={form.control}
              render={({ field }) => (
                <div className="space-y-2 mb-2">
                  <span className="text-xs font-medium">Data Source</span>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="includeEstimates"
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked);
                      }}
                      data-dd-action-name="toggle-estimated-data"
                      aria-label="Include estimated data"
                    />
                    <label htmlFor="includeEstimates" className="text-sm">
                      Include estimated data
                    </label>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button type="button" className="flex items-center" aria-label="View member distribution information" data-dd-action-name="view-member-distribution-info">
                          <CircleInfoIcon className="h-4 w-4" style={{ color: '#CBD1D7' }} aria-hidden="true" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent role="tooltip" aria-live="polite">
                        <TooltipHeader>
                          <TooltipTitle>Estimated Data</TooltipTitle>
                        </TooltipHeader>
                        <TooltipBody>
                          <TooltipItem>
                            <TooltipItemValue>
                              Estimated data are for creators whose social accounts are not authenticated with Aspire. These data are typically calculated using the creator's follower count and historical data.
                            </TooltipItemValue>
                          </TooltipItem>
                        </TooltipBody>
                        <TooltipArrow />
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>
              )}
            />
          </div>
        </Form>
      </div>
      <div className="flex justify-start gap-2 p-4 mt-auto mb-4 border-t bg-background mx-[-1.5rem]">
        <Button type="submit" data-dd-action-name="social-analytics-filters-apply">Apply</Button>
        <Button type="button" variant="outline" onClick={onCancel} data-dd-action-name="social-analytics-filters-cancel">
          Cancel
        </Button>
      </div>
    </form>
  );
};
